import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Select,
} from "@chakra-ui/react";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Question } from "../../models/Question";
import useCreateOrEditQuestion from "../../hooks/api/question/useCreateOrEditQuestion";

type Props = {
  question?: Question;
  quizId?: string;
  questionId?: string;
  onSuccess?: (lessonId: number) => void;
  onError?: () => void;
};

type FormValues = {
  optA: string;
  optB: string;
  optC: string;
  optD: string;
  solution: string;
  description: string;
};

export default function QuestionForm({
  question,
  quizId,
  questionId,
  onSuccess,
  onError,
}: Props) {
  const { t } = useTranslation();
  const {
    handleSubmit,
    register,
    formState: { errors, isValid, isSubmitted },
  } = useForm<FormValues>({
    defaultValues: {
      optA: question?.options["A"] || "",
      optB: question?.options["B"] || "",
      optC: question?.options["C"] || "",
      optD: question?.options["D"] || "",
      solution: question?.solutions[0] || "",
      description: question?.description || "",
    },
  });

  const { mutateAsync: createOrEditQuestion, isPending } =
    useCreateOrEditQuestion(quizId ?? "", questionId ?? "");

  const onSubmit = useCallback(
    (values: FormValues) => {
      createOrEditQuestion({
        question: {
          id: question?.id,
          description: values.description,
          solutions: [values.solution],
          options: {
            A: values.optA,
            B: values.optB,
            C: values.optC,
            D: values.optD,
          },
          type: "MULTIPLE_CHOICE",
        },
        operation: question ? "edit" : "create",
      })
        .then((response) => onSuccess?.(response.id))
        .catch(onError);
    },
    [createOrEditQuestion, question, onError, onSuccess],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormControl
        isInvalid={!isValid && isSubmitted}
        display="flex"
        flexDirection="column"
        gap={4}
      >
        <div>
          <FormLabel htmlFor="description">{t("forms.description")}</FormLabel>
          <Input
            id="description"
            placeholder={t("forms.description")}
            {...register("description", {
              required: t("forms.validation.required"),
            })}
          />
          <FormErrorMessage>
            {errors.description && errors.description.message}
          </FormErrorMessage>
        </div>
        <div>
          <FormLabel htmlFor="optA">{t("forms.option") + " A"}</FormLabel>
          <Input
            id="optA"
            {...register("optA", {
              required: t("forms.validation.required"),
            })}
          />
          <FormErrorMessage>
            {errors.description && errors.description.message}
          </FormErrorMessage>
        </div>
        <div>
          <FormLabel htmlFor="optB">{t("forms.option") + " B"}</FormLabel>
          <Input
            id="optB"
            {...register("optB", {
              required: t("forms.validation.required"),
            })}
          />
          <FormErrorMessage>
            {errors.description && errors.description.message}
          </FormErrorMessage>
        </div>
        <div>
          <FormLabel htmlFor="optC">{t("forms.option") + " C"}</FormLabel>
          <Input
            id="optC"
            {...register("optC", {
              required: t("forms.validation.required"),
            })}
          />
          <FormErrorMessage>
            {errors.description && errors.description.message}
          </FormErrorMessage>
        </div>
        <div>
          <FormLabel htmlFor="optD">{t("forms.option") + " D"}</FormLabel>
          <Input
            id="optD"
            {...register("optD", {
              required: t("forms.validation.required"),
            })}
          />
          <FormErrorMessage>
            {errors.description && errors.description.message}
          </FormErrorMessage>
        </div>
        <div>
          <FormLabel htmlFor="schedule">{t("forms.solution")}</FormLabel>
          <Select
            id="solution"
            {...register("solution", {})}
            defaultValue={question?.solutions[0]}
          >
            <option value=""></option>
            <option value="A">A</option>
            <option value="B">B</option>
            <option value="C">C</option>
            <option value="D">D</option>
          </Select>
        </div>
      </FormControl>
      <Button
        mt={4}
        bg="primary.DEFAULT"
        color="white"
        isLoading={isPending}
        type="submit"
      >
        {t("common.buttons.save")}
      </Button>
    </form>
  );
}
